import { useEffect, useRef, useState } from 'react';
// pdf.js v4 ships ESM only — `.mjs` is the canonical worker. Vite resolves
// `?url` at build time and emits a hashed asset; that URL is what pdf.js
// fetches to spawn its Worker. Importing the URL (not the module) keeps the
// worker bundle out of the main JS chunk.
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * Renders an arbitrary PDF byte stream into a vertical stack of `<canvas>`
 * elements via pdf.js. Used by `PdfSurveyForm` to show a live preview of
 * the filled-in PDF (all pages) as the candidate types / signs. pdf.js is
 * loaded dynamically so the heavy main bundle isn't paid for unless a PDF
 * survey actually opens.
 */

let pdfjsModulePromise: Promise<any> | null = null;
const loadPdfJs = async (): Promise<any> => {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = (async () => {
      const mod: any = await import('pdfjs-dist');
      if (typeof window !== 'undefined') {
        mod.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;
      }
      return mod;
    })();
  }
  return pdfjsModulePromise;
};

interface UsePdfPreviewArgs {
  /** Bytes to render. `null` keeps the container empty. */
  bytes: Uint8Array | null;
  /** Pixel scale. Higher = sharper but slower. Defaults to 1.5. */
  scale?: number;
}

interface UsePdfPreviewResult {
  /** Attach to the `<div>` that should host the page canvases. */
  containerRef: (node: HTMLDivElement | null) => void;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  pageCount: number;
}

export const usePdfPreview = ({
  bytes,
  scale = 1.5,
}: UsePdfPreviewArgs): UsePdfPreviewResult => {
  const [status, setStatus] = useState<UsePdfPreviewResult['status']>('idle');
  const [error, setError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  // Monotonic token so stale async renders bail out before mutating the DOM.
  const renderTokenRef = useRef(0);

  const containerRef = (node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
  };

  useEffect(() => {
    let cancelled = false;
    const token = ++renderTokenRef.current;

    if (!bytes || bytes.length === 0) {
      setStatus('idle');
      setError(null);
      setPageCount(0);
      if (containerNodeRef.current) containerNodeRef.current.innerHTML = '';
      return;
    }

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        // pdf.js consumes (and may detach) the buffer, so clone first.
        const slice = new Uint8Array(bytes);
        const loadingTask = pdfjsLib.getDocument({ data: slice });
        const pdf = await loadingTask.promise;
        if (cancelled || token !== renderTokenRef.current) return;

        const container = containerNodeRef.current;
        if (!container) return;

        // Build the new canvases off-DOM first, then swap in one shot to
        // avoid the flash of empty container between clear and append.
        const fragment = document.createDocumentFragment();
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled || token !== renderTokenRef.current) return;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = 'block';
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          if (i > 1) canvas.style.marginTop = '12px';

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          fragment.appendChild(canvas);

          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled || token !== renderTokenRef.current) return;
        }

        // Re-check the container — it might have unmounted while rendering.
        const liveContainer = containerNodeRef.current;
        if (!liveContainer) return;
        liveContainer.innerHTML = '';
        liveContainer.appendChild(fragment);

        setPageCount(pdf.numPages);
        setStatus('ready');
      } catch (err: any) {
        if (cancelled || token !== renderTokenRef.current) return;
        setStatus('error');
        setError(err?.message || 'Failed to render preview.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bytes, scale]);

  return { containerRef, status, error, pageCount };
};
